/**
 * Shared auto-advance engine for A+ content rows with autoplay: true
 * (type: slideshow today; any future auto-advancing row reuses the same
 * markup contract instead of bespoke per-row timer/progress-bar code).
 *
 * Markup contract (built by snippets/atomberg-aplus-auto-slideshow.liquid):
 *   .atomberg-auto-slideshow[data-interval][data-animation]
 *     .atomberg-auto-slideshow__track
 *       .atomberg-auto-slideshow__slide (one per item, first has .is-active)
 *     .atomberg-auto-slideshow__controls
 *       .atomberg-auto-slideshow__segments
 *         .atomberg-auto-slideshow__segment button (one per item)
 *           .atomberg-auto-slideshow__segment-fill
 *       .atomberg-auto-slideshow__pause (optional, card-slider preset)
 *         — decorative SVG ring (not a control, matching SSR); its dash
 *           animation restarts each advance via .is-running
 *
 * A single requestAnimationFrame ticker per instance (instead of
 * setTimeout/clearTimeout) drives both the slide advance and the CSS fill
 * animation's pause state from the same elapsed-time clock, so a
 * hover-pause can never drift the visual fill out of sync with when the
 * next slide actually advances.
 */
(function () {
  /** @type {any} */
  var w = window;
  if (w._atombergAplusAutoAdvanceInit) return;
  w._atombergAplusAutoAdvanceInit = true;

  var DEFAULT_INTERVAL_MS = 5000;

  /**
   * @typedef {{
   *   root: HTMLElement,
   *   track: HTMLElement,
   *   slides: HTMLElement[],
   *   segments: HTMLElement[],
   *   interval: number,
   *   activeIndex: number,
   *   elapsed: number,
   *   paused: boolean,
   *   lastTick: number,
   *   raf: number,
   *   pauseBtn: HTMLElement | null,
   * }} AutoSlideshowState
   */

  /**
   * @param {AutoSlideshowState} state
   * @param {number} index
   */
  function goTo(state, index) {
    var count = state.slides.length;
    var next = ((index % count) + count) % count;

    var current = state.slides[state.activeIndex];
    if (current) current.classList.remove('is-active');
    var upcoming = state.slides[next];
    if (upcoming) upcoming.classList.add('is-active');
    state.track.style.setProperty('--auto-index', String(next));

    state.segments.forEach(function (segment, i) {
      segment.classList.remove('is-active', 'is-done');
      if (i < next) segment.classList.add('is-done');
    });
    // Force a reflow so a segment re-activated after a full loop restarts
    // its CSS fill animation from 0% instead of being treated as unchanged.
    var activeSegment = state.segments[next];
    if (activeSegment) {
      void activeSegment.offsetWidth;
      activeSegment.classList.add('is-active');
    }

    if (state.pauseBtn) {
      // Same reflow trick: the ring's dash animation runs `forwards` per
      // slide, so it must be restarted from 0 on every advance/jump.
      state.pauseBtn.classList.remove('is-running');
      void state.pauseBtn.offsetWidth;
      state.pauseBtn.classList.add('is-running');
    }

    state.activeIndex = next;
    state.elapsed = 0;
  }

  /**
   * @param {AutoSlideshowState} state
   * @param {number} now
   */
  function tick(state, now) {
    // data-aplus-offscreen is set by the shared IntersectionObserver below;
    // no advancing while the slideshow isn't visible.
    if (!state.paused && !state.root.hasAttribute('data-aplus-offscreen')) {
      state.elapsed += now - state.lastTick;
      if (state.elapsed >= state.interval) {
        goTo(state, state.activeIndex + 1);
      }
    }
    state.lastTick = now;
    state.raf = requestAnimationFrame(function (n) {
      tick(state, n);
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    if (root.dataset.atombergAutoWired === 'true') return;
    root.dataset.atombergAutoWired = 'true';

    var track = /** @type {HTMLElement|null} */ (
      root.querySelector('.atomberg-auto-slideshow__track')
    );
    if (!track) return;
    var slides = /** @type {HTMLElement[]} */ (
      Array.prototype.slice.call(track.querySelectorAll('.atomberg-auto-slideshow__slide'))
    );
    var segments = /** @type {HTMLElement[]} */ (
      Array.prototype.slice.call(root.querySelectorAll('.atomberg-auto-slideshow__segment'))
    );
    if (slides.length < 2) return;

    var interval = parseInt(root.dataset.interval || '', 10);
    if (!interval || interval < 500) interval = DEFAULT_INTERVAL_MS;

    var startIndex = slides.findIndex(function (s) {
      return s.classList.contains('is-active');
    });

    var pauseBtn = /** @type {HTMLElement|null} */ (
      root.querySelector('.atomberg-auto-slideshow__pause')
    );
    if (pauseBtn) pauseBtn.classList.add('is-running');

    /** @type {AutoSlideshowState} */
    var state = {
      root: root,
      track: track,
      slides: slides,
      segments: segments,
      interval: interval,
      activeIndex: startIndex >= 0 ? startIndex : 0,
      elapsed: 0,
      paused: false,
      lastTick: performance.now(),
      raf: 0,
      pauseBtn: pauseBtn,
    };
    track.style.setProperty('--auto-index', String(state.activeIndex));

    segments.forEach(function (segment, i) {
      segment.addEventListener('click', function () {
        goTo(state, i);
      });
    });

    /** @param {boolean} paused */
    function syncPaused(paused) {
      state.paused = paused;
      if (paused) {
        root.setAttribute('data-paused', '');
      } else {
        root.removeAttribute('data-paused');
      }
    }
    function hoverPause() {
      syncPaused(true);
    }
    function hoverResume() {
      syncPaused(false);
    }
    // Hover-pause is scoped to the CONTROLS, not the whole component: a
    // cursor resting anywhere over full-width media silently froze autoplay
    // (looked broken whenever the slider was fully in view). Gated on
    // (hover: hover) because touch taps emit a synthetic mouseenter with no
    // matching mouseleave, which would stick the pause on after tapping a
    // segment.
    var controls = /** @type {HTMLElement|null} */ (
      root.querySelector('.atomberg-auto-slideshow__controls')
    );
    if (controls && window.matchMedia('(hover: hover)').matches) {
      controls.addEventListener('mouseenter', hoverPause);
      controls.addEventListener('mouseleave', hoverResume);
    }
    root.addEventListener('touchstart', hoverPause, { passive: true });
    root.addEventListener('touchend', hoverResume);
    root.addEventListener('touchcancel', hoverResume);
    state.raf = requestAnimationFrame(function (now) {
      tick(state, now);
    });
  }

  /**
   * Offscreen = frozen: sets data-aplus-offscreen on every animated A+
   * container so CSS can flip --aplus-play to paused (crossfade layers,
   * zoom, lines, rings), the rAF ticker above stops advancing, and autoplay
   * videos are suspended — N infinite animations running below the fold
   * otherwise burn main-thread/GPU time for nothing.
   * @type {IntersectionObserver | null}
   */
  var motionObserver = null;

  function observeMotion() {
    if (!('IntersectionObserver' in window)) return;
    if (!motionObserver) {
      motionObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var el = /** @type {HTMLElement} */ (entry.target);
            if (entry.isIntersecting) {
              el.removeAttribute('data-aplus-offscreen');
            } else {
              el.setAttribute('data-aplus-offscreen', '');
            }
            var video = /** @type {HTMLVideoElement|null} */ (el.querySelector('video[autoplay]'));
            if (video) {
              if (entry.isIntersecting) {
                var resumed = video.play();
                // Autoplay policy can reject; muted background video, safe to ignore.
                if (resumed && resumed.catch) resumed.catch(function () {});
              } else {
                video.pause();
              }
            }
          });
        },
        // Same lead-in the SSR site uses for its in-viewport gating
        { rootMargin: '200px 0px' }
      );
    }
    document
      .querySelectorAll(
        '.atomberg-auto-slideshow, .atomberg-aplus__crossfade, .atomberg-aplus__split, .atomberg-aplus__zoom-row, .atomberg-aplus__video-row'
      )
      .forEach(function (el) {
        // A crossfade inside a split row must pause with the ROW, not on its own
        // box: the lines pill + panel colour live in the sibling text panel, so a
        // separately-timed pause on the image box drifts them out of phase.
        if (
          el.classList.contains('atomberg-aplus__crossfade') &&
          el.parentElement &&
          el.parentElement.closest('.atomberg-aplus__split')
        ) {
          return;
        }
        if (/** @type {HTMLElement} */ (el).dataset.atombergMotionWatched === 'true') return;
        /** @type {HTMLElement} */ (el).dataset.atombergMotionWatched = 'true';
        /** @type {IntersectionObserver} */ (motionObserver).observe(el);
      });
  }

  function initAll() {
    document.querySelectorAll('.atomberg-auto-slideshow').forEach(function (root) {
      init(/** @type {HTMLElement} */ (root));
    });
    observeMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:load', initAll);
})();
